/**
 * Environment configuration system
 * Loads configuration based on the current environment (development or production)
 * Uses environment variables to keep API keys secure
 */

// Check if we're in development or production mode
// TypeScript augmentation for Vite env variables
/**
 * @typedef {Object} ImportMetaEnv
 * @property {string} MODE
 * @property {string} VITE_APP_ENV
 * @property {string} VITE_FIREBASE_API_KEY
 * @property {string} VITE_FIREBASE_AUTH_DOMAIN
 * @property {string} VITE_FIREBASE_PROJECT_ID
 * @property {string} VITE_FIREBASE_STORAGE_BUCKET
 * @property {string} VITE_FIREBASE_MESSAGING_SENDER_ID
 * @property {string} VITE_FIREBASE_APP_ID
 * @property {string} VITE_FIREBASE_MEASUREMENT_ID
 * @property {string} VITE_USE_EMULATORS
 * @property {string} VITE_EMULATOR_HOST
 */

// For Vite, we need to use import.meta.env instead of process.env
// @ts-ignore - Vite provides these at runtime
const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.VITE_APP_ENV === 'development';

// Default environment - used as fallback
const defaultEnv = {
  name: isDevelopment ? 'development' : 'production',
  
  // Firebase config - defaults for production
  firebase: {
    // @ts-ignore - Vite provides these at runtime
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDgR2KutNVH5dSw0-0RAMilQHZUd3-sJJs",
    // @ts-ignore - Vite provides these at runtime
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "storybook-wizard-452216.firebaseapp.com",
    // @ts-ignore - Vite provides these at runtime
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "storybook-wizard-452216",
    // @ts-ignore - Vite provides these at runtime
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "storybook-wizard-452216.firebasestorage.app",
    // @ts-ignore - Vite provides these at runtime
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "527901597296",
    // @ts-ignore - Vite provides these at runtime
    appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:527901597296:web:2001c8b9fef6c785329eb5",
    // @ts-ignore - Vite provides these at runtime
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-122QNY9NMT",
  },
  
  // Lulu API config
  lulu: {
    // Always use the correct base URL based on sandbox flag
    baseUrl: isDevelopment ? 'https://api.sandbox.lulu.com' : 'https://api.lulu.com',
    tokenUrl: '/auth/realms/glasstree/protocol/openid-connect/token',
    printJobCostUrl: '/print-job-cost-calculations/',
    // No auth header here: every Lulu call goes through /.netlify/functions/lulu-*,
    // which read LULU_AUTH_HEADER server-side. Any VITE_* value is inlined into the
    // public bundle, which is how both Lulu credentials leaked.
    // Flag to determine if we're using Lulu sandbox or production
    USE_SANDBOX: isDevelopment,
  }
};

// Create a reactive store for the current environment
let currentEnv = { ...defaultEnv };
const listeners = [];

/**
 * Subscribe to environment changes
 * @param {Function} listener - Called when environment changes
 * @returns {Function} Unsubscribe function
 */
export const subscribeToEnvChanges = (listener) => {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
};

/**
 * Notify all listeners that environment has changed
 */
const notifyListeners = () => {
  listeners.forEach(listener => listener(currentEnv));
};

/**
 * Switch between development and production environments
 * @param {boolean} useDevelopment - Whether to use development environment
 */
export const switchEnvironment = (useDevelopment) => {
  // Store the selection in localStorage
  localStorage.setItem('STORYBOOK_WIZARD_ENV', useDevelopment ? 'development' : 'production');
  
  // Update the current environment
  // When useDevelopment is true, use sandbox; when false, use production
  const luluUseSandbox = useDevelopment;
  console.log(`🔄 Switching environment - Development: ${useDevelopment}, Lulu API: ${luluUseSandbox ? 'SANDBOX' : 'PRODUCTION'}`);
  
  currentEnv = {
    ...currentEnv,
    name: useDevelopment ? 'development' : 'production',
    lulu: {
      ...currentEnv.lulu,
      USE_SANDBOX: luluUseSandbox, // Use sandbox when in development
      baseUrl: luluUseSandbox ? 'https://api.sandbox.lulu.com' : 'https://api.lulu.com',
    }
  };
  
  // Notify all subscribers
  notifyListeners();
  
  return currentEnv;
};

/**
 * Initialize environment based on localStorage or defaults
 */
export const initializeEnvironment = () => {
  const storedEnv = localStorage.getItem('STORYBOOK_WIZARD_ENV');
  if (storedEnv) {
    switchEnvironment(storedEnv === 'development');
  }
  return getCurrentEnvironment();
};

/**
 * Get the current environment configuration
 * @returns {Object} The current environment
 */
export const getCurrentEnvironment = () => {
  return currentEnv;
};

// Initialize from localStorage on module load
initializeEnvironment();

export default {
  getCurrentEnvironment,
  switchEnvironment,
  subscribeToEnvChanges,
  initializeEnvironment,
};
